import { useCallback, useId, useMemo, useRef } from 'react';
import { useButton } from 'react-aria';
import cn from 'classnames';
import { HStack, IconArrowDown, IconClose, Text, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { add, subtract } from '@adv-frontend/yastore-modules/lib';
import { type OrderIncentiveType } from '@/entities/order';
import { formatPrice } from '@/shared/lib/formatPrice';
import { usePromoCodeController } from '../PromoCodeInput/PromoCodeInput';
import styles from './Benefits.module.css';

interface BenefitsProps {
    orderId: string;
    discount: number;
    promocodeDiscount?: string;
    giftCardDiscount?: string;
    giftCardBalance?: string;
    defaultExpanded?: boolean;
    promocodeTitle?: string;
    bonuses?: number;
    yaPayDiscount?: string;
}

export const Benefits = (props: BenefitsProps) => {
    const {
        discount,
        promocodeTitle,
        promocodeDiscount,
        giftCardDiscount,
        giftCardBalance,
        bonuses,
        yaPayDiscount,
        defaultExpanded = true,
    } = props;
    const { toggle, isOpen } = useToggle(defaultExpanded);
    const shouldShowDiscount =
        add(
            add(add(discount, promocodeDiscount ? promocodeDiscount : 0), bonuses ? bonuses : 0),
            giftCardDiscount ? giftCardDiscount : 0
        ) < 0;

    const id = useId();
    const ref = useRef<HTMLDivElement>(null);

    const { buttonProps } = useButton(
        {
            ...props,
            onPress: () => toggle(),
            elementType: 'header',
        },
        ref
    );

    const ariaProps = useMemo(() => {
        return {
            'aria-expanded': isOpen,
            'aria-controls': id,
        };
    }, [id, isOpen]);

    const controller = usePromoCodeController();

    const remove = useCallback(
        (incentiveType: OrderIncentiveType) => {
            controller.onRemovePromoCode(incentiveType);
        },
        [controller]
    );

    const regularDiscount = useMemo(() => {
        let result = discount;

        if (bonuses) {
            result = add(result, bonuses);
        }

        if (promocodeDiscount) {
            result = add(result, promocodeDiscount);
        }

        if (giftCardDiscount) {
            result = add(result, giftCardDiscount);
        }

        return result;
    }, [bonuses, discount, giftCardDiscount, promocodeDiscount]);

    const countOfExpandedItems = useMemo(
        () =>
            (shouldShowDiscount ? 1 : 0) +
            (promocodeDiscount ? 1 : 0) +
            (bonuses ? 1 : 0) +
            (giftCardDiscount ? 1 : 0) +
            (yaPayDiscount ? 1 : 0),
        [bonuses, giftCardDiscount, promocodeDiscount, shouldShowDiscount, yaPayDiscount]
    );

    return (
        <VStack gap="4">
            <HStack justifyContent="space-between">
                <HStack
                    {...buttonProps}
                    {...ariaProps}
                    alignItems="center"
                    justifyContent="center"
                    gap="4"
                    className={styles.collapseButton}
                >
                    <Text size="s">Выгода</Text>{' '}
                    <IconArrowDown
                        size="8"
                        className={cn(styles.collapseIcon, {
                            [styles.collapseIconExpanded]: isOpen,
                        })}
                    />
                </HStack>
                <Text size="s" color="red">
                    {formatPrice(subtract(discount, yaPayDiscount || 0))}
                </Text>
            </HStack>
            <VStack
                gap="4"
                className={cn(
                    styles.benefits,
                    {
                        [styles.expandedOneItem]: isOpen && countOfExpandedItems === 1,
                        [styles.expandedTwoItems]: isOpen && countOfExpandedItems === 2,
                        [styles.expandedThreeItems]: isOpen && countOfExpandedItems === 3,
                        [styles.expandedFourItems]: isOpen && countOfExpandedItems === 4,
                        [styles.expandedFiveItems]: isOpen && countOfExpandedItems === 5,
                    },
                    {}
                )}
            >
                {shouldShowDiscount && (
                    <HStack justifyContent="space-between">
                        <HStack className={styles.benefitsItem} alignItems="center" justifyContent="center" gap="4">
                            <Text size="s">Скидка</Text>
                        </HStack>
                        <Text size="s">{formatPrice(regularDiscount)}</Text>
                    </HStack>
                )}

                {yaPayDiscount && yaPayDiscount !== '0' && (
                    <HStack justifyContent="space-between">
                        <HStack className={styles.benefitsItem} alignItems="center" justifyContent="center" gap="4">
                            <Text size="s">Скидка Яндекс Пэй</Text>
                        </HStack>
                        <Text size="s">{formatPrice(subtract(0, yaPayDiscount))}</Text>
                    </HStack>
                )}

                {bonuses && (
                    <HStack justifyContent="space-between">
                        <HStack className={styles.benefitsItem} alignItems="center" justifyContent="center" gap="4">
                            <Text size="s">Бонусы</Text>
                        </HStack>
                        <Text size="s">{formatPrice(subtract(0, bonuses))}</Text>
                    </HStack>
                )}

                {promocodeDiscount && (
                    <HStack justifyContent="space-between">
                        <HStack className={styles.benefitsItem} alignItems="center" justifyContent="center" gap="4">
                            <Text size="s">Промокод - {promocodeTitle}</Text>
                            <IconClose size="8" cursor="pointer" onClick={() => remove('PROMOCODE')} />
                        </HStack>
                        <Text size="s">{formatPrice(subtract(0, promocodeDiscount))}</Text>
                    </HStack>
                )}

                {giftCardDiscount && (
                    <HStack justifyContent="space-between">
                        <HStack className={styles.benefitsItem} alignItems="center" justifyContent="center" gap="4">
                            <Text size="s">
                                Подарочная карта
                                {giftCardBalance ? ` (Баланс: ${formatPrice(giftCardBalance)})` : ''}
                            </Text>{' '}
                            <IconClose size="8" cursor="pointer" onClick={() => remove('GIFT_CARD')} />
                        </HStack>
                        <Text size="s">{formatPrice(subtract(0, giftCardDiscount))}</Text>
                    </HStack>
                )}
            </VStack>
        </VStack>
    );
};
